import {Trans} from '@ui/i18n/trans';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {FormSwitch} from '@ui/forms/toggle/switch';
import {useForm} from 'react-hook-form';
import {Form} from '@ui/forms/form';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import React, {Fragment, useEffect, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {Skeleton} from '@ui/skeleton/skeleton';
import {queryClient} from '@common/http/query-client';
import {DatatableDataQueryKey} from '@common/datatable/requests/paginated-resources';
import {RequestTitle} from '@app/titles/models/Request';
import {
  UpdateRequestPayload,
  useUpdateRequest,
} from '@app/admin/requetes/requests/use-update-request';
import {
  CreateRequestPayload,
  useCreateRequest,
} from '@app/admin/requetes/requests/use-create-request';
import {FormChipField} from '@ui/forms/input-field/chip-field/form-chip-field';
import {CategorySelect} from '@common/ui/categories/category-select';
import {RequestContentSearchField} from '@app/admin/requetes/requests/request-content-search-field';
import {getTitle} from '@app/admin/titles/get-title';
import {Title} from '@app/titles/models/title';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {RequestEditorLayout} from '@app/admin/requetes/lien-editor/request-editor-layout';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {useRequest} from '@app/admin/requetes/requests/use-request';

export function RequestPrimaryFactsForm({requestId}: {requestId?: number}) {
  const request = useRequest(requestId);
  return (
    <FileUploadProvider>
      {requestId ? (
        request?.data?.request ? (
          <EditRequestForm request={request.data.request} />
        ) : (
          <Skeleton />
        )
      ) : (
        <CreateRequestForm />
      )}
    </FileUploadProvider>
  );
}

function CreateRequestForm() {
  const form = useForm<CreateRequestPayload>({
    defaultValues: {
      serie: false,
    },
  });
  const createRequest = useCreateRequest(form);
  const [titleId, setTitleId] = useState(0);
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const {close, formId} = useDialogContext();
  return (
    <Dialog className="min-w-[300px]" size="2xl">
      <DialogHeader padding="px-14 py-10">
        <Trans message="New Request" />
      </DialogHeader>
      <DialogBody padding="p-0">
        <Form
          form={form}
          onSubmit={values => {
            createRequest.mutate(
              {...values, title_id: titleId},
              {
                onSuccess: response => {
                  toast(message('Request added'));
                  close();
                },
              },
            );
          }}
        >
          <RequestEditorLayout
            actions={
              <Button
                variant="flat"
                color="primary"
                type="submit"
                disabled={createRequest.isPending || !isDirty}
              >
                <Trans message="Create" />
              </Button>
            }
          >
            <FormFields onSucess={id => setTitleId(id)} create={true} />
          </RequestEditorLayout>
        </Form>
      </DialogBody>
    </Dialog>
  );
}

interface EditRequestFormProps {
  request: RequestTitle;
}

function EditRequestForm({request}: EditRequestFormProps) {
  const navigate = useNavigate();
  const [titleId, setTitleId] = useState(request?.title_id || undefined);
  const form = useForm<UpdateRequestPayload>({
    defaultValues: {
      author: '',
      title: '',
      category: 2,
      saison: '',
      episodes: [1],
      resolver: '',
    },
  });

  useEffect(() => {
    if (request) {
      form.reset({
        author: request.author,
        title: request.title,
        title_id: request.title_id,
        category: request.category,
        saison: request.saison,
        serie: request.serie,
        full_saison: request.full_saison,
        episodes: request.episodes,
        resolver: request.resolver,
        exit_title: !!request.title_id,
        url: request.url,
      });
    }
  }, [request, form]);
  const updateRequest = useUpdateRequest(request.id, form);
  const {close, formId} = useDialogContext();
  return (
    <Dialog className="min-w-[300px]" size="2xl">
      <DialogHeader padding="px-14 py-10">
        <Trans values={{id: request.id}} message="Edit request Id “:id“" />
      </DialogHeader>
      <DialogBody padding="p-0">
        <Form
          form={form}
          onSubmit={values => {
            updateRequest.mutate(
              {...values, title_id: titleId},
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: DatatableDataQueryKey('requests'),
                  });
                  close();
                },
              },
            );
          }}
        >
          <RequestEditorLayout
            actions={
              <Button
                variant="flat"
                color="primary"
                type="submit"
                disabled={updateRequest.isPending || !form.formState.isDirty}
              >
                <Trans message="Save" />
              </Button>
            }
          >
            <FormFields
              onSucess={id => setTitleId(id)}
              create={false}
              request={request}
            />
          </RequestEditorLayout>
        </Form>
      </DialogBody>
    </Dialog>
  );
}

function FormFields({
  request,
  create,
  onSucess,
}: {
  create: boolean;
  request?: RequestTitle;
  onSucess?: (id: number) => void;
}) {
  const [existTitle, setExistTitle] = useState(!!request?.title_id);
  const [serie, setSerie] = useState(request?.serie || false);
  const [fullSaison, setfullSaison] = useState(request?.full_saison || false);
  const [title, setTitle] = useState(request?.post);
  const [titleId, setTitleId] = useState(request?.title_id || undefined);

  useEffect(() => {
    if (title && title.is_series !== undefined) {
      setSerie(title.is_series);
    }
  }, [title]);
  if (!create && !request) return <Skeleton className="mb-24" />;
  return (
    <Fragment>
      <div>
        <FormSwitch
          value={existTitle ? 1 : 0}
          onChange={e => setExistTitle(e.target.checked)}
          name="exit_title"
          className="mb-24"
        >
          <Trans message="Does the title exist on the site?" />
        </FormSwitch>
      </div>
      <div>
        <div>
          <CategorySelect category={request?.category} />
        </div>
        <div>
          {existTitle ? (
            <RequestContentSearchField
              titleId={titleId}
              onChange={value => {
                getTitle(value, 'title').then(data => {
                  if (data.title) {
                    setTitle(data.title);
                    setTitleId(data.title.id);
                    onSucess?.(data.title.id);
                  }
                });
              }}
            />
          ) : (
            <FormTextField
              name="title"
              label={<Trans message={'Title'} />}
              className={'my-24'}
              type="text"
            />
          )}
        </div>
        {!existTitle && (
          <div>
            <FormTextField
              name="url"
              label={<Trans message="Url About Title" />}
              type="text"
              className="my-24"
            />
          </div>
        )}
      </div>
      <div>
        {!existTitle ? (
          <FormSwitch
            checked={serie}
            onChange={() => setSerie(serie => !serie)}
            name="serie"
            className="mb-24"
          >
            <Trans message="Is it a series?" />
          </FormSwitch>
        ) : null}
        {serie ? (
          <FormSwitch
            onChange={() => setfullSaison(fullSaison => !fullSaison)}
            defaultValue={fullSaison ? 1 : 0}
            name="full_saison"
            className="my-24"
          >
            <Trans message="Full season?" />
          </FormSwitch>
        ) : null}
      </div>
      {serie ? (
        <div>
          <FormTextField
            name="saison"
            label={<Trans message="Season" />}
            type="text"
            className="my-24"
          />
          {!fullSaison && <EpisodeForm />}
        </div>
      ) : null}
    </Fragment>
  );
}

function EpisodeForm() {
  const [query, setQuery] = useState('');
  return (
    <Fragment>
      <FormChipField
        type={'number'}
        className="w-full"
        name={'episodes'}
        valueKey="name"
        label={<Trans message={'Episodes'} />}
        inputValue={query}
        onInputValueChange={setQuery}
      ></FormChipField>
      <span className={'text-gray-500 text-xs text-muted'}>
        <Trans
          message={
            "Enter the number of episode and press 'Enter', Max : 3 Episodes"
          }
        />
      </span>
    </Fragment>
  );
}
